import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { getNightOrDayPalette } from '../../../../../utils/getNightOrDayPalette';
import { Box, Button, FormHelperText, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

type Props = Readonly<{
	label: JSX.Element;
	error: boolean | JSX.Element;
	value: number | null | undefined;
	setValue: Dispatch<SetStateAction<number | null>>;
}>;

export const NumberInputBasic: FC<Props> = ({ label, error, value, setValue }) => {
	const intl = useIntl();
	const theme = useTheme();
	const [shownValue, setShownValue] = useState(value != null ? value.toString() : '');

	const decrementValue = () => {
		setShownValue((Number(shownValue) - 1).toString());
		setValue(Number(shownValue) - 1);
	};

	const incrementValue = () => {
		setShownValue((Number(shownValue) + 1).toString());
		setValue(Number(shownValue) + 1);
	};

	const handleNumberChange = (value: string) => {
		const isValidInput = /^[0-9]*([.,][0-9]*)?$/.test(value);
		const hasOnlyOneCommaOrDot = /^[^,]*([,.][^,.]*){0,1}$/.test(value);

		if (!isValidInput || !hasOnlyOneCommaOrDot) {
			return;
		}

		setShownValue(value);
		setValue(Number(value.replaceAll(',', '.')));
	};

	useEffect(() => {
		if (value != null && value.toString() !== shownValue) {
			setShownValue(value.toString());
		}
	}, [setValue, shownValue, value]);

	return (
		<Box>
			<Typography>{label}</Typography>
			<Stack direction={'row'} maxWidth={220}>
				<TextField
					error={Boolean(error)}
					placeholder={'Type a number…'}
					value={shownValue}
					onChange={(event) => handleNumberChange(event.target.value)}
					InputProps={{
						endAdornment: (
							<Stack
								display={'flex'}
								position={'absolute'}
								height={'100%'}
								width={25}
								alignItems={'center'}
								justifyContent={'center'}
								right={1}
								direction={'column'}
								overflow={'hidden'}
								borderLeft={`1px solid ${theme.palette[getNightOrDayPalette(theme)][300]}`}
							>
								<Button
									variant={'text'}
									sx={{
										height: 21,
										fontSize: '0.5rem',
										background: theme.palette[getNightOrDayPalette(theme)][0],
										color: theme.palette[getNightOrDayPalette(theme)][400],
										borderBottom: `1px solid ${theme.palette[getNightOrDayPalette(theme)][300]}`,
									}}
									onClick={incrementValue}
								>
									{/* eslint-disable-next-line */}
									{'▲'}
								</Button>
								<Button
									variant={'text'}
									sx={{
										height: 21,
										fontSize: '0.5rem',
										background: theme.palette[getNightOrDayPalette(theme)][0],
										color: theme.palette[getNightOrDayPalette(theme)][400],
										transform: 'rotate(180deg)',
									}}
									onClick={decrementValue}
								>
									{/* eslint-disable-next-line */}
									{'▲'}
								</Button>
							</Stack>
						),
					}}
				/>
				<Button
					onClick={() => {
						setShownValue('');
						setValue(null);
					}}
				>
					<Typography color={theme.palette[getNightOrDayPalette(theme)][900]}>{intl.formatMessage({ id: 'common.clear' })}</Typography>
				</Button>
			</Stack>
			{error && <FormHelperText error>{error}</FormHelperText>}
		</Box>
	);
};
